import React, { useContext, useCallback, useMemo, useEffect, useState } from 'react';
import Header from 'components/header/Header';
import {
  DisplayModes,
  GetDisplayMode,
  navigationBlockWarningMessage,
  SetDisplayMode,
  isNullOrUndefined,
} from 'utils/Global';
import {
  ExperimentHeaderProps,
  ROIClientDTO,
  WellDetailsStates,
} from 'business/models/CustomModels';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import {
  selectIsEditable,
  selectedDrawingState,
  selectROIs,
} from 'redux/selectors/StateSelectors';
import {
  setDropImageForKey,
  setIsWellImagingEnabled,
} from 'redux/reducers/experimentReducer';
import { ExpDashboardContext } from './ExperimentDashboardContext';
import { usageStatLogger } from 'components/usagestat/UsageStat';
import './ExperimentHeaderStyles.scss';
import ExperimentCommandButtons from './ExperimentCommandButtons';
import {
  getAddLayerList,
  GetAddLayerListParams,
  getDesignHeaderButtons,
  GetDesignHeaderButtonsParams,
  getImageHeaderButtons,
  GetImageHeaderButtonsParams,
  getInfoHeaderButtons,
  GetInfoHeaderButtonsParams,
  getBarcodeActionsList,
  getPrintActionsList,
  getReportsButtonsList,
  GetReportsButtonsListParams,
  getScoreHeaderButtons,
  GetScoreHeaderButtonsParams,
  getScreenDesignHeaderButtons,
  GetScreenDesignHeaderButtonsParams,
  getWellImageHeaderButtons,
  GetWellImageHeaderButtonsParams,
  screenStockReportLabel,
} from 'components/Experiment/utils';
import { EDrawingType } from 'components/plateviewer/well/Well';
import {
  BooleanPropertyDTO,
  PlateDropRegions,
  WellSelectionDTO,
} from 'business/models/Models';
import { FileExportView } from 'components/plateviewer/experiment/FileExportView';
import { EFileExportTypes, EFileImportTypes } from 'enums/EFileExportTypes';
import { useLicenseAuthorizeCommand } from 'CustomHooks/useLicenseAuthorizeCommand';
import { EUserAction } from 'constants/index';
import useRMLite from 'CustomHooks/useRMLite';
import WellService from 'business/services/WellService';
import { useModalHandlers } from 'components/modal/hooks';
import { ManageImages } from './Setup/Imaging/ManageImages';
import { setWellDropsImagingEnabled } from 'redux/reducers/experimentDataReducer';
import { usePlateDisableImageRegions } from 'components/commandpanel/Navigator/hooks';
import {
  useAllRegions,
  useAllWellNavigationDetails,
  useLastSelectedWellNavigationDetails,
  useScoreExperimentWellsInProgressCount,
  useScoresForWells,
  useSelectedDropImageParameters,
  useWellDropImportFlToggle,
} from 'redux/hooks/experiment';
import { useIsScreenInUse } from 'redux/hooks/common';
import { useExperimentRightClickOptions } from 'components/rightClick/hooks/experimentHooks';
import { hasModifyAccess } from 'utils/accessRights';
import {
  useInspectionChange,
  useIsWellImagingEnabled,
  useMarkInteresting,
  useMarkInterestingWellDrop,
  useToggleWellDropImage,
} from 'components/Experiment/hooks';
import { useIgnoreAutoScoreWellDrop } from 'CustomHooks/useIgnoreAutoScoreWellDrop';
import { EReportTypes } from 'enums/EIconPanels';
import DropImageSettings from 'components/commandpanel/ExperimentPanel/ExperimentTools/DropImageSettings';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import { PropertyConstants } from 'enums/PropertyConstants';
import {
  ELayerDesignSelectedTabIndex,
  setLayerDesignSelectedTabIndex,
} from 'redux/reducers/layerReducer';
import { ESidePanelTabs } from './constants';
import {
  useSaveDiscardButtons,
  UseSaveDiscardButtonsParams,
} from 'CustomHooks/useSaveDiscardButtons';
import { useFocusLevelImages } from 'components/well/hooks';
import { getWellDropImageUniqueKeyByWellDetails } from 'redux/utils/well';
import { getRegionIdCached } from 'components/well/utils';
import { useCurrentDropImage } from 'redux/hooks/well';
import { buildFocusLevelOptions } from './utils';
import { useEditBarcodeBulkModal } from 'components/customComponents/Experiment/EditBarcodeBulk/useEditBarcodeBulkModal';
import { IGNORE_AUTO_SCORE_MESSAGES } from 'constants/messages/ignoreAutoScore';

export default function ExperimentHeader({
  expName,
  changeCompareMode,
  headerType,
  saveExperiment,
  onDiscardButtonClick,
  changeIconPanels,
  isDirty,
  accessRightName,
  setShowOptimize,
  setReportType,
  pathName,
  setShowSeedFormulation,
  saveImageClick,
  editExperimentClick,
  sidePanelHandlers,
  setImageView,
  setScoreView,
  exportPoi,
  deleteInspection,
  printBarcode,
  isPasteNotAllowed,
  isHeaderButtonsDisabled,
  rotateCanvas,
  markAsDispensed,
  exportFrapDataPoints,
  disableFrapExport,
  isDropImageAvailable,
  disablePrintBarcode,
  breadCrumbs,
  inspections,
  isImagedExperiment,
  isResourceAvailable,
  selectedCompareMode,
}: ExperimentHeaderProps) {
  const wellNavigationDetails = useLastSelectedWellNavigationDetails();
  const selectedDropImageParameters = useSelectedDropImageParameters();
  const allWellNavigationDetails = useAllWellNavigationDetails();
  const regions = useAllRegions();
  const { wellNum, plateID, plateNum } = wellNavigationDetails || {};
  const { cpId, dropNumber, regionName, inspection } = selectedDropImageParameters || {};
  const regionID = getRegionIdCached(plateID, wellNum, dropNumber, regionName, regions);
  const isEditMode: boolean = useRMSelector(selectIsEditable);
  const isWellImageEnable = useIsWellImagingEnabled(dropNumber);
  const selectedDrawing = useRMSelector(selectedDrawingState);
  const getPlateDisableImageRegions = usePlateDisableImageRegions();
  const wellDropRegions = getPlateDisableImageRegions(plateID);
  const rois: ROIClientDTO[] = useRMSelector((state) =>
    selectROIs(state, dropNumber),
  ) as ROIClientDTO[];
  const dispatch = useRMDispatch();
  const displayMode = GetDisplayMode();
  const isCompareMode = pathName ? pathName.includes('/compare') : false;
  const isScreenMode = pathName ? pathName.includes('/screen') : false;
  const isWellMode = pathName ? pathName.includes('/well') : false;
  const expDashboardContext = useContext(ExpDashboardContext);
  const expData = useRMSelector((state) => state.experiment.expData);
  const licenseAuthCommand = useLicenseAuthorizeCommand();
  const rmLite = useRMLite();
  const { showModal, hideModal } = useModalHandlers();
  const isScreenInUse: boolean = useIsScreenInUse();
  const setExperimentRightClick = useExperimentRightClickOptions();
  const isEditingAllowed = useSystemProperty<BooleanPropertyDTO>(
    PropertyConstants.AllowEditingOfReadonlyExperiment,
    false,
  );
  const isEditBarcodeAllowed = useSystemProperty<BooleanPropertyDTO>(
    'ExperimentPlate.AllowBarcodeEdit',
    false,
  );
  const { showEditBarcodeModal, isEditBarcodeDisabled } =
    useEditBarcodeBulkModal(isEditBarcodeAllowed);
  const focusLevelImages = useFocusLevelImages(
    wellNavigationDetails,
    selectedDropImageParameters,
  );
  const wellDetails = {
    wellNum,
    plateNum,
    plateID,
    cpID: cpId,
    dropNumber,
    inspectionSet: inspection,
    regionID,
    imageBatchId: inspection?.groupedInspections?.find((gi) => gi.plateId === plateID)
      ?.imageBatchId,
  } as WellDetailsStates;
  const UIKey = getWellDropImageUniqueKeyByWellDetails(wellDetails);
  const currentDropImage = useCurrentDropImage();

  const [experimentClipboardState, setExperimentClipboardState] = useState({
    experimentCopy: null,
    experimentPaste: null,
    isCopyDisabled: false,
    isPasteDisabled: true,
  });

  const onInspectionChange = useInspectionChange(inspections);
  const [importFl, setImportFl] = useWellDropImportFlToggle();
  const toggleWellDropImage = useToggleWellDropImage();
  const markInterestingWellDrop = useMarkInterestingWellDrop();
  const markInteresting = useMarkInteresting();
  const lastSelectedWell =
    allWellNavigationDetails?.[allWellNavigationDetails.length - 1];
  const experimentImageBatchId = inspection?.groupedInspections?.find(
    (grp) => grp.plateId === lastSelectedWell?.plateID,
  )?.imageBatchId;
  const experimentWellScores = useScoresForWells(
    lastSelectedWell
      ? [
          {
            wellNumber: lastSelectedWell.wellNum,
            plateId: lastSelectedWell.plateID,
            dropNumber,
          },
        ]
      : [],
  );
  const ignoreAutoScoreParams = useMemo(() => {
    if (
      isNullOrUndefined(lastSelectedWell?.plateID) ||
      isNullOrUndefined(experimentImageBatchId) ||
      isNullOrUndefined(lastSelectedWell?.wellNum) ||
      isNullOrUndefined(dropNumber)
    ) {
      return null;
    }

    return {
      plateId: lastSelectedWell.plateID,
      imageBatchId: experimentImageBatchId,
      wellNumber: lastSelectedWell.wellNum,
      dropNumber,
    };
  }, [lastSelectedWell, experimentImageBatchId, dropNumber]);
  const {
    showIgnoreAutoScoreConfirmation,
    isIgnoreAutoScoreDisabled,
    isAutoScoreIgnored,
  } = useIgnoreAutoScoreWellDrop({
    scores: experimentWellScores,
    params: ignoreAutoScoreParams,
    isDisabled: allWellNavigationDetails?.length > 1,
    refreshExperimentScores: true,
  });
  const scoreExperimentWellsInProgressCount = useScoreExperimentWellsInProgressCount();

  const openLayersExplorerPanel = useCallback(() => {
    dispatch(setLayerDesignSelectedTabIndex(ELayerDesignSelectedTabIndex.Explorer));
    expDashboardContext.setSelectedSidePanelTab(ESidePanelTabs.Layers);
  }, [expDashboardContext.setSelectedSidePanelTab]);

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const editExperimentClickHandler = useCallback(
    () => editExperimentClick(),
    [editExperimentClick],
  );
  const ShowOptimizeModalHandler = licenseAuthCommand(
    () => setShowOptimize(true),
    EUserAction.Optimization,
  );

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const createSeedHandler = useCallback(() => setShowSeedFormulation(true), []);

  const isSelectedROIImaged = useCallback(() => {
    const region = rois.find((r) => r.id === selectedDrawing.id);
    if (region) {
      return region.imagingEnabled;
    }
    return true;
  }, [rois, selectedDrawing]);

  const getDisableImageLabel = () => {
    let label = '';
    if (displayMode === DisplayModes.IMAGE) {
      const getImageEnable = (x: PlateDropRegions) => {
        return (
          x.wellNumber === wellNum &&
          x.dropNumber === dropNumber &&
          x.regionName === regionName
        );
      };
      const imgEnabled = wellDropRegions?.filter(getImageEnable)[0]?.imagingEnabled;
      label = imgEnabled ? 'Disable Imaging' : 'Enable Imaging';
      dispatch(setIsWellImagingEnabled(imgEnabled));
    } else if (displayMode === DisplayModes.WELLIMAGE) {
      if (selectedDrawing?.type === EDrawingType.ROI) {
        label = isSelectedROIImaged() ? 'Disable Imaging' : 'Enable Imaging';
      } else {
        label = isWellImageEnable ? 'Disable Imaging' : 'Enable Imaging';
      }
    }
    return label;
  };

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const toggleWellDropImaging = useCallback(async () => {
    dispatch(
      setWellDropsImagingEnabled({
        wellDropRegions: [
          { plateId: plateID, wellNumber: wellNum, dropNumber: dropNumber },
        ],
        imagingEnabled: !isWellImageEnable,
      }),
    );
    await new WellService().setWellDropImagingEnabled(
      [
        {
          plateId: plateID,
          wellNumber: wellNum,
          dropNumber: dropNumber,
        },
      ] as WellSelectionDTO[],
      !isWellImageEnable,
    );
  }, [setWellDropsImagingEnabled, plateID, wellNum, dropNumber, isWellImageEnable]);

  const manageImaging = async () => {
    showModal({
      body: <ManageImages hideModal={hideModal} expTreeNodeId={expData.treeNodeId} />,
      heading: 'Manage Images',
      dialogClassName: 'min-w-[492px] m-0',
      isCancelOnly: false,
      showButtons: false,
      hideFooter: true,
    });
  };

  const reportsListParams: GetReportsButtonsListParams = useMemo(
    () => ({
      accessRightName,
      GetDisplayMode,
      setReportType,
      usageStatLogger,
      licenseAuthCommand,
      SetDisplayMode,
      exportFrapDataPoints,
      disableFrapExport,
    }),
    [
      accessRightName,
      GetDisplayMode,
      setReportType,
      usageStatLogger,
      licenseAuthCommand,
      SetDisplayMode,
      exportFrapDataPoints,
      disableFrapExport,
    ],
  );

  const addLayerListParams: GetAddLayerListParams = useMemo(
    () => ({
      accessRightName,
      changeIconPanels,
      isEditMode,
      isScreenMode,
      authCommand: licenseAuthCommand,
      isScreenInUse,
      openLayersExplorerPanel,
    }),
    [
      accessRightName,
      changeIconPanels,
      isEditMode,
      isScreenMode,
      licenseAuthCommand,
      isScreenInUse,
      openLayersExplorerPanel,
    ],
  );

  const saveDiscardButtonsParams: UseSaveDiscardButtonsParams = {
    enableButtons: isDirty && !isHeaderButtonsDisabled,
    onSave: saveExperiment,
    onDiscard: onDiscardButtonClick,
    navigationBlockCallback: (location) => {
      const urlParts = location?.pathname.split('/');
      if (
        (!location ||
          (urlParts.length > 1 && urlParts[1] !== 'exp' && urlParts[1] !== 'screen')) &&
        isResourceAvailable
      ) {
        if (isDirty) {
          return navigationBlockWarningMessage;
        } else if (scoreExperimentWellsInProgressCount) {
          return 'There are pending scoring operations. Are you sure you want to leave?';
        }
      }
    },
  };

  /** Adding call back/useMemo to this list is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const reportsList = useMemo(
    () => getReportsButtonsList(reportsListParams),
    [reportsListParams],
  );

  /** Adding call back/useMemo to this list is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const addLayerList = useMemo(
    () => getAddLayerList(addLayerListParams),
    [addLayerListParams],
  );

  const SaveDiscardButtons = useSaveDiscardButtons(saveDiscardButtonsParams);

  const printActionsList = useMemo(
    () => getPrintActionsList(printBarcode),
    [printBarcode, plateID],
  );

  const barcodeActionsList = useMemo(
    () =>
      getBarcodeActionsList(showEditBarcodeModal, printBarcode, {
        isEditBarcodeDisabled:
          rmLite ||
          isEditBarcodeDisabled ||
          isHeaderButtonsDisabled ||
          !hasModifyAccess(accessRightName),
      }),
    [
      showEditBarcodeModal,
      printBarcode,
      rmLite,
      isEditBarcodeDisabled,
      isHeaderButtonsDisabled,
      accessRightName,
    ],
  );

  const designHeaderButtonsParams: GetDesignHeaderButtonsParams = {
    addLayerList,
    reportsList,
    printActionsList,
    editExperiment: editExperimentClickHandler,
    showOptimizationModal: ShowOptimizeModalHandler,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    setFileImportActionType: expDashboardContext.setFileImportActionType,
    printBarcode,
    accessRightName,
    isEditMode,
    isDisabled: isHeaderButtonsDisabled,
    isIngredientView: false,
    markAsDispensed,
    disablePrintBarcode,
    isEditingAllowed: isEditingAllowed,
    isRMLite: rmLite,
  };

  const imageHeaderButtonsParams: GetImageHeaderButtonsParams = {
    saveImage: saveImageClick,
    changeIconPanels,
    ShowOptimizeModalHandler,
    reportsList,
    accessRightName,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    exportPoi,
    deleteInspection,
    createSeedHandler,
    isDisabled: isHeaderButtonsDisabled,
    getDisableImageLabel,
    setRegionImaging: toggleWellDropImaging,
    rmLite,
    manageImaging,
  };

  const scoreHeaderButtonsParams: GetScoreHeaderButtonsParams = {
    ShowOptimizeModalHandler,
    reportsList,
    accessRightName,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    exportPoi,
    isDisabled: isHeaderButtonsDisabled,
    rmLite,
  };

  const wellImageButtonsParams: GetWellImageHeaderButtonsParams = {
    isCompareMode,
    isWellMode,
    compareModeHandler: changeCompareMode,
    accessRightName,
    createSeedHandler,
    ShowOptimizeModalHandler,
    saveImage: saveImageClick,
    reportsList,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    isDisabled: isHeaderButtonsDisabled,
    getDisableImageLabel,
    setRegionImaging: toggleWellDropImaging,
    rmLite,
    exportPoi,
    isDropImageAvailable,
    selectedCompareMode,
  };

  const infoHeaderButtonsParams: GetInfoHeaderButtonsParams = {
    reportsList,
    barcodeActionsList,
    printBarcode,
    isDisabled: isHeaderButtonsDisabled,
    disablePrintBarcode,
    rmLite,
  };

  const screenDesignHeaderButtonsParams: GetScreenDesignHeaderButtonsParams = {
    accessRightName,
    addLayerList,
    setFileExportActionType: expDashboardContext.setFileExportActionType,
    setFileImportActionType: expDashboardContext.setFileImportActionType,
    reportsList,
    printActionsList,
    showOptimizationModal: ShowOptimizeModalHandler,
    printBarcode,
    isDisabled: isHeaderButtonsDisabled,
    rotateCanvas,
    disablePrintBarcode,
  };

  const designHeaderButtons = getDesignHeaderButtons({
    ...designHeaderButtonsParams,
    isIngredientView: displayMode === DisplayModes.WELLINGREDIENT ? true : false,
    isDispensed: expData?.isDispensed,
  });
  const imageHeaderButtons = getImageHeaderButtons(imageHeaderButtonsParams);
  const scoreHeaderButtons = getScoreHeaderButtons(scoreHeaderButtonsParams);
  const wellImageHeaderButtons = getWellImageHeaderButtons(wellImageButtonsParams);
  const infoHeaderButtons = getInfoHeaderButtons(infoHeaderButtonsParams);
  const screenDesignHeaderButtons = getScreenDesignHeaderButtons(
    screenDesignHeaderButtonsParams,
  );

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const inspectionMenuItems = useMemo(() => {
    return inspections?.map((item) => ({
      label: item.name,
      onClick: () => onInspectionChange(item.id),
      isSelected: item.id === inspection?.id,
      shouldShowSelectedItemCheckmark: true,
    }));
  }, [inspections, inspection]);

  const dropImageSettings = (
    <DropImageSettings
      customizeSettingsClassName=""
      disabled={false}
      canvasMode={true}
      wrapperClassName="!w-[400px]"
    />
  );

  /** Adding call back/useMemo to this method is used in experiment right click,
   if we dont memoize it, then it will cause infinite re-rendering
   due to new refernece each time. Handle with caution */
  const exportOptions = useMemo(
    () => [
      {
        label: 'Export XML Screen',
        onClick: () =>
          expDashboardContext.setFileExportActionType(EFileExportTypes.XMLSCREEN),
        isDisabled: !hasModifyAccess(accessRightName),
      },
      {
        label: 'Export Scores',
        onClick: licenseAuthCommand((e) => {
          setReportType(EReportTypes.ScoreReport);
          usageStatLogger('Report.' + e.target.textContent);
        }, EUserAction.ScoreReport),
        isDisabled: !hasModifyAccess(accessRightName),
      },
      {
        label: 'Export Selected Wells',
        onClick: () => expDashboardContext.setFileExportActionType(EFileExportTypes.Text),
        isDisabled: !hasModifyAccess(accessRightName),
      },
    ],
    [expDashboardContext.setFileExportActionType, accessRightName, licenseAuthCommand],
  );

  const focusLevelOptions = useMemo(
    () =>
      buildFocusLevelOptions(focusLevelImages, currentDropImage?.imageId, (flImage) =>
        dispatch(
          setDropImageForKey({
            key: UIKey,
            image: { ...flImage, image: flImage.image } as any,
          }),
        ),
      ),
    [focusLevelImages, currentDropImage, UIKey],
  );

  /** Only add methods which are memoized, otherwise it can cause infinite re-rendering.
  Deal with caution */

  const actionMap = useMemo(
    () => ({
      'Add Layer Tool': {
        action: () => {},
        subMenu: addLayerList,
      },
      ...(isEditingAllowed && {
        'Edit Experiment': {
          action: editExperimentClickHandler,
          isDisabled: isEditMode,
        },
      }),
      'Import XML Screen': {
        action: () => {
          expDashboardContext.setFileImportActionType(EFileImportTypes.XMLSCREEN);
        },
        isDisabled: !isEditMode || !hasModifyAccess(accessRightName),
      },
      Export: {
        action: () => {},
        subMenu: exportOptions,
      },
      Reports: {
        action: () => {},
        subMenu: reportsList.filter((item) => item.label != screenStockReportLabel),
      },
      'New Seed': {
        action: createSeedHandler,
        isDisabled: isHeaderButtonsDisabled,
      },
      'Mark as Dispensed': {
        action: markAsDispensed,
        isDisabled:
          expData?.isDispensed ||
          !markAsDispensed ||
          !hasModifyAccess(accessRightName) ||
          isHeaderButtonsDisabled,
      },
      Copy: {
        action: experimentClipboardState.experimentCopy,
        isDisabled: experimentClipboardState.isCopyDisabled,
      },
      Paste: {
        action: experimentClipboardState.experimentPaste,
        isDisabled: experimentClipboardState.isPasteDisabled,
      },
      Inspections: {
        action: () => {},
        subMenu: inspectionMenuItems,
      },
      'Save Image': {
        action: saveImageClick,
      },
      [IGNORE_AUTO_SCORE_MESSAGES.label]: {
        action: showIgnoreAutoScoreConfirmation,
        isSelected: isAutoScoreIgnored,
        isDisabled: isIgnoreAutoScoreDisabled,
      },
      'Disable Image': {
        action: toggleWellDropImage,
        isSelected: isImagedExperiment ? !isWellImageEnable : false,
        isDisabled: !isImagedExperiment,
      },
      'Enable Import Focus Level': {
        action: setImportFl,
        isSelected: isImagedExperiment ? importFl : false,
        isDisabled: !isImagedExperiment,
      },
      'Drop Captures': {
        action: () => {},
        customSubmenuComponent: dropImageSettings,
      },
      'Mark Interesting': {
        action: () => markInterestingWellDrop('i', markInteresting),
        isSelected: markInteresting,
      },
      'Instant Drop Report': {
        action: licenseAuthCommand((e) => {
          setReportType(EReportTypes.InstantDropReport);
          usageStatLogger('Report.' + e.target.textContent);
        }, EUserAction.InstantDropReport),
        isDisabled: !hasModifyAccess(accessRightName),
      },
      'Single Drop Report': {
        isDisabled: !hasModifyAccess(accessRightName),
        action: licenseAuthCommand((e) => {
          setReportType(EReportTypes.SingleDropReport);
          usageStatLogger('Report.' + e.target.textContent);
        }, EUserAction.SingleDropReport),
      },
      'Select Focus Level': {
        isDisabled: !focusLevelOptions,
        action: () => {},
        subMenu: focusLevelOptions,
      },
    }),
    [
      addLayerList,
      editExperimentClickHandler,
      isEditMode,
      isEditingAllowed,
      expDashboardContext.setFileImportActionType,
      expDashboardContext.setFileExportActionType,
      reportsList,
      createSeedHandler,
      markAsDispensed,
      experimentClipboardState,
      inspectionMenuItems,
      saveImageClick,
      toggleWellDropImage,
      setImportFl,
      markInterestingWellDrop,
      setReportType,
      licenseAuthCommand,
      markInteresting,
      showIgnoreAutoScoreConfirmation,
      isIgnoreAutoScoreDisabled,
      isAutoScoreIgnored,
    ],
  );

  useEffect(() => {
    if (!isScreenMode) {
      setExperimentRightClick(actionMap, displayMode);
    }
  }, [actionMap, expName]);

  function getHeader(name) {
    let view;
    switch (name) {
      case DisplayModes.DESIGN:
        // to do: We need to handel the Screen and Design mode properly, currently its mixed up
        view = isScreenMode ? (
          <Header
            headerButtons={screenDesignHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={SaveDiscardButtons}
            extendedCommandSection={
              <ExperimentCommandButtons
                isDisabled={isHeaderButtonsDisabled}
                isPasteNotAllowed={isPasteNotAllowed}
                setExperimentClipboardState={setExperimentClipboardState}
              />
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        ) : (
          <Header
            headerButtons={designHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={SaveDiscardButtons}
            extendedCommandSection={
              <ExperimentCommandButtons
                isDisabled={isHeaderButtonsDisabled}
                isPasteNotAllowed={isPasteNotAllowed}
                setExperimentClipboardState={setExperimentClipboardState}
              />
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.WELLINGREDIENT:
        view = (
          <Header
            headerButtons={designHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={!rmLite ? SaveDiscardButtons : []}
            extendedCommandSection={
              !rmLite ? (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              ) : null
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.IMAGE:
        view = (
          <Header
            headerButtons={imageHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedCommandSection={
              !rmLite ? (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              ) : null
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.SCORE:
        view = (
          <Header
            headerButtons={scoreHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedCommandSection={
              !rmLite ? (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              ) : null
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.WELLIMAGE:
        view = (
          <Header
            headerButtons={wellImageHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedCommandSection={
              !isWellMode &&
              !rmLite && (
                <ExperimentCommandButtons
                  isDisabled={isHeaderButtonsDisabled}
                  isPasteNotAllowed={isPasteNotAllowed}
                  setExperimentClipboardState={setExperimentClipboardState}
                />
              )
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.EXPINFO:
        view = (
          <Header
            headerButtons={infoHeaderButtons}
            title={expName}
            isNewTheme={true}
            extendedActionSection={SaveDiscardButtons}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.SCREEN:
        view = (
          <Header
            headerButtons={screenDesignHeaderButtons}
            title={expName}
            extendedSection={sidePanelHandlers}
            extendedActionSection={SaveDiscardButtons}
            extendedCommandSection={
              <ExperimentCommandButtons
                isDisabled={isHeaderButtonsDisabled}
                isPasteNotAllowed={isPasteNotAllowed}
                setExperimentClipboardState={setExperimentClipboardState}
              />
            }
            isNewTheme={true}
            breadCrumbs={breadCrumbs}
          />
        );
        break;
      case DisplayModes.REPORT:
        view = <Header headerButtons={[]} title={expName} />;
        break;
      default:
        break;
    }
    return view;
  }

  return (
    <>
      {getHeader(headerType)}

      {expDashboardContext.fileExportActionType != EFileExportTypes.NONE && (
        <FileExportView
          exportType={expDashboardContext.fileExportActionType}
          setExportType={expDashboardContext.setFileExportActionType}
          selectedWells={allWellNavigationDetails}
          expData={expData}
        />
      )}
    </>
  );
}
